import { useState } from 'react';
import { api } from '../lib/api';
import { Button, FormField } from '../components/ui';

// Which certs are required based on property answers
function getRequiredChecks(answers) {
  const checks = [];

  // Always required
  checks.push({ key: 'epc',         label: 'EPC',                        why: 'Required for all rented properties — must be rated E or above', freq: 'Every 10 years' });
  checks.push({ key: 'eicr',        label: 'EICR',                       why: 'Electrical Installation Condition Report — mandatory for all lets', freq: 'Every 5 years' });
  checks.push({ key: 'smoke_alarm', label: 'Smoke Alarms',               why: 'Required on every floor of the property', freq: 'Test monthly, check annually' });
  checks.push({ key: 'co_alarm',    label: 'CO Alarm',                   why: 'Required in rooms with solid fuel appliances', freq: 'Test monthly' });
  checks.push({ key: 'legionella',  label: 'Legionella Risk Assessment', why: 'Landlord duty under Health & Safety at Work Act', freq: 'Every 2 years or on change of tenancy' });

  // Only if gas appliances
  if (answers.hasGas) {
    checks.push({ key: 'gas_safety', label: 'Gas Safety Certificate', why: 'Mandatory if property has any gas appliances or supply', freq: 'Every year' });
  }

  return checks;
}

function getRisks(answers, certStatuses) {
  const risks = [];

  // HMO licensing risk
  if (answers.occupants >= 5 && answers.households >= 2) {
    risks.push({ level: 'high', text: 'Mandatory HMO licence required (5+ occupants, 2+ households). Fine up to £30,000 without one.' });
  } else if (answers.occupants >= 3 && answers.households >= 2) {
    risks.push({ level: 'medium', text: 'Property may require additional or selective HMO licensing — check with your local council.' });
  }

  // EPC rating risk
  if (answers.epcRating && ['F', 'G'].includes(answers.epcRating.toUpperCase())) {
    risks.push({ level: 'high', text: `EPC rating ${answers.epcRating.toUpperCase()} is below the legal minimum of E. You cannot legally let this property without improvement.` });
  }

  // Missing certs
  const required = getRequiredChecks(answers);
  required.forEach(c => {
    const status = certStatuses[c.key];
    if (status === 'missing') risks.push({ level: 'high',   text: `${c.label} is missing. ${c.why}.` });
    if (status === 'expired') risks.push({ level: 'high',   text: `${c.label} has expired and must be renewed immediately.` });
    if (status === 'due_soon') risks.push({ level: 'medium', text: `${c.label} is expiring soon — schedule renewal now.` });
  });

  // Right to Rent
  if (!answers.rightToRentDone) {
    risks.push({ level: 'high', text: 'Right to Rent checks not confirmed. Fines up to £20,000 per tenant without valid checks.' });
  }

  // No tenancy agreement
  if (!answers.hasTenancyAgreement) {
    risks.push({ level: 'medium', text: 'No written tenancy agreement recorded. Strongly recommended to protect both parties.' });
  }

  return risks;
}

const STEPS = ['Property', 'Occupancy', 'Certificates', 'Tenancy', 'Rent', 'Results'];

const riskColors = {
  high:   { bg: 'var(--danger-lt)', color: 'var(--danger)', icon: '🚨' },
  medium: { bg: 'var(--warn-lt)',   color: 'var(--warn)',   icon: '⚠️' },
  low:    { bg: 'var(--accent-lt)', color: 'var(--accent)', icon: '✅' },
};

export default function PropertyWizard({ onComplete, onCancel }) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [createdPropertyId, setCreatedPropertyId] = useState(null);

  // Step 1 — property basics
  const [property, setProperty] = useState({
    address_line1: '', address_line2: '', city: '', postcode: '',
    council_area: '', property_type: 'single_let', bedrooms: 1,
  });

  // Step 2 — occupancy
  const [occupancy, setOccupancy] = useState({
    occupants: 1, households: 1, hasGas: true,
  });

  // Step 3 — cert statuses (missing | valid | expired | due_soon) + expiry dates
  const [certStatuses, setCertStatuses] = useState({});
  const [certDates, setCertDates] = useState({});
  const [epcRating, setEpcRating] = useState('');

  // Step 4 — tenancy
  const [tenancy, setTenancy] = useState({
    hasTenancyAgreement: false, rightToRentDone: false,
  });

  const answers = { ...occupancy, epcRating, rightToRentDone: tenancy.rightToRentDone, hasTenancyAgreement: tenancy.hasTenancyAgreement };
  const requiredChecks = getRequiredChecks(answers);
  const risks = getRisks(answers, certStatuses);
  const highRisks   = risks.filter(r => r.level === 'high');
  const mediumRisks = risks.filter(r => r.level === 'medium');

  const setProp = k => e => setProperty(p => ({ ...p, [k]: e.target.value }));
  const setOcc  = k => e => setOccupancy(o => ({ ...o, [k]: e.target.type === 'checkbox' ? e.target.checked : +e.target.value }));

  async function saveAndFinish() {
    setSaving(true);
    try {
      // Create property if not already created
      let propId = createdPropertyId;
      if (!propId) {
        const created = await api.createProperty(property);
        propId = created.id;
        setCreatedPropertyId(propId);
      }

      // Save certificates that have dates
      for (const check of requiredChecks) {
        const status = certStatuses[check.key];
        const expiry = certDates[`${check.key}_expiry`];
        const issued = certDates[`${check.key}_issued`];
        if (status && status !== 'missing' && expiry) {
          await api.createCertificate(propId, {
            cert_type: check.key,
            issued_date: issued || '',
            expiry_date: expiry,
          });
        }
      }

      onComplete(propId);
    } finally {
      setSaving(false);
    }
  }

  // Progress bar
  const progress = ((step) / (STEPS.length - 1)) * 100;

  return (
    <div style={{ maxWidth: 640, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 30, fontWeight: 300 }}>Property <em>Setup Wizard</em></h1>
        <p style={{ color: 'var(--ink-3)', marginTop: 4 }}>We'll identify every compliance requirement for your property</p>
      </div>

      {/* Progress */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          {STEPS.map((s, i) => (
            <span key={s} style={{ fontSize: 12, fontWeight: i === step ? 600 : 400, color: i <= step ? 'var(--accent)' : 'var(--ink-3)' }}>{s}</span>
          ))}
        </div>
        <div style={{ height: 4, background: 'var(--border)', borderRadius: 2 }}>
          <div style={{ height: '100%', width: `${progress}%`, background: 'var(--accent)', borderRadius: 2, transition: 'width .3s ease' }} />
        </div>
      </div>

      {/* Step content */}
      <div style={{ background: 'var(--bg-card)', border: '1.5px solid var(--border)', borderRadius: 14, padding: 32, marginBottom: 24 }}>

        {/* STEP 1 — Property basics */}
        {step === 0 && (
          <div className="fade-up">
            <h2 style={{ fontSize: 20, marginBottom: 20 }}>Tell us about the property</h2>
            <FormField label="Address line 1">
              <input value={property.address_line1} onChange={setProp('address_line1')} placeholder="123 Example Street" required />
            </FormField>
            <FormField label="Address line 2 (optional)">
              <input value={property.address_line2} onChange={setProp('address_line2')} />
            </FormField>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <FormField label="City">
                <input value={property.city} onChange={setProp('city')} required />
              </FormField>
              <FormField label="Postcode">
                <input value={property.postcode} onChange={setProp('postcode')} required />
              </FormField>
            </div>
            <FormField label="Council area (optional)">
              <input value={property.council_area} onChange={setProp('council_area')} placeholder="e.g. Newham, Liverpool" />
            </FormField>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <FormField label="Property type">
                <select value={property.property_type} onChange={setProp('property_type')}>
                  {['single_let','hmo','flat','house'].map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
                </select>
              </FormField>
              <FormField label="Bedrooms">
                <input type="number" min={1} value={property.bedrooms} onChange={e => setProperty(p => ({ ...p, bedrooms: +e.target.value }))} />
              </FormField>
            </div>
          </div>
        )}

        {/* STEP 2 — Occupancy */}
        {step === 1 && (
          <div className="fade-up">
            <h2 style={{ fontSize: 20, marginBottom: 8 }}>Occupancy & utilities</h2>
            <p style={{ fontSize: 14, color: 'var(--ink-3)', marginBottom: 20 }}>This helps us identify HMO licensing requirements</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <FormField label="Total occupants">
                <input type="number" min={1} value={occupancy.occupants} onChange={setOcc('occupants')} />
              </FormField>
              <FormField label="Number of households">
                <input type="number" min={1} value={occupancy.households} onChange={setOcc('households')} />
              </FormField>
            </div>
            <p style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 20 }}>A "household" means a family unit or single person. Multiple unrelated sharers = multiple households.</p>

            {/* HMO flag */}
            {occupancy.occupants >= 5 && occupancy.households >= 2 && (
              <div style={{ background: 'var(--danger-lt)', border: '1px solid var(--danger)', borderRadius: 8, padding: '12px 16px', marginBottom: 20 }}>
                <p style={{ color: 'var(--danger)', fontWeight: 600, fontSize: 14 }}>🚨 Mandatory HMO licence likely required</p>
                <p style={{ color: 'var(--danger)', fontSize: 13, marginTop: 4 }}>5+ occupants from 2+ households triggers mandatory HMO licensing. Fine up to £30,000 without one.</p>
              </div>
            )}

            <FormField label="">
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <input type="checkbox" checked={occupancy.hasGas} onChange={setOcc('hasGas')} style={{ width: 'auto' }} />
                <span style={{ fontSize: 14 }}>Property has gas appliances or gas supply</span>
              </label>
            </FormField>
            <p style={{ fontSize: 12, color: 'var(--ink-3)' }}>This determines whether a Gas Safety Certificate is required</p>
          </div>
        )}

        {/* STEP 3 — Certificates */}
        {step === 2 && (
          <div className="fade-up">
            <h2 style={{ fontSize: 20, marginBottom: 8 }}>Certificate status</h2>
            <p style={{ fontSize: 14, color: 'var(--ink-3)', marginBottom: 24 }}>Based on your property, here's what's legally required. Tell us the current status of each.</p>

            {requiredChecks.map(check => (
              <div key={check.key} style={{ marginBottom: 24, paddingBottom: 24, borderBottom: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div>
                    <p style={{ fontWeight: 600, fontSize: 15 }}>{check.label}</p>
                    <p style={{ fontSize: 12, color: 'var(--ink-3)' }}>{check.freq}</p>
                  </div>
                </div>
                <p style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 12 }}>{check.why}</p>

                {/* EPC — also ask for rating */}
                {check.key === 'epc' && (
                  <FormField label="EPC rating (A–G)">
                    <select value={epcRating} onChange={e => setEpcRating(e.target.value)} style={{ marginBottom: 8 }}>
                      <option value="">Select rating</option>
                      {['A','B','C','D','E','F','G'].map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </FormField>
                )}

                <FormField label="Do you have this certificate?">
                  <select
                    value={certStatuses[check.key] || ''}
                    onChange={e => setCertStatuses(s => ({ ...s, [check.key]: e.target.value }))}
                  >
                    <option value="">Select...</option>
                    <option value="missing">No — I don't have one</option>
                    <option value="valid">Yes — it's current</option>
                    <option value="expired">Yes — but it's expired</option>
                    <option value="due_soon">Yes — but expiring within 30 days</option>
                  </select>
                </FormField>

                {certStatuses[check.key] && certStatuses[check.key] !== 'missing' && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 8 }}>
                    <FormField label="Issue date (optional)">
                      <input type="date" value={certDates[`${check.key}_issued`] || ''} onChange={e => setCertDates(d => ({ ...d, [`${check.key}_issued`]: e.target.value }))} />
                    </FormField>
                    <FormField label="Expiry date">
                      <input type="date" value={certDates[`${check.key}_expiry`] || ''} onChange={e => setCertDates(d => ({ ...d, [`${check.key}_expiry`]: e.target.value }))} />
                    </FormField>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* STEP 4 — Tenancy */}
        {step === 3 && (
          <div className="fade-up">
            <h2 style={{ fontSize: 20, marginBottom: 20 }}>Tenancy & legal checks</h2>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }}>
                <input type="checkbox" checked={tenancy.hasTenancyAgreement} onChange={e => setTenancy(t => ({ ...t, hasTenancyAgreement: e.target.checked }))} style={{ width: 'auto', marginTop: 2 }} />
                <div>
                  <p style={{ fontWeight: 500, fontSize: 14 }}>I have a written tenancy agreement in place</p>
                  <p style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>AST or other written agreement signed by all tenants</p>
                </div>
              </label>
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }}>
                <input type="checkbox" checked={tenancy.rightToRentDone} onChange={e => setTenancy(t => ({ ...t, rightToRentDone: e.target.checked }))} style={{ width: 'auto', marginTop: 2 }} />
                <div>
                  <p style={{ fontWeight: 500, fontSize: 14 }}>Right to Rent checks completed for all tenants</p>
                  <p style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>Original documents verified before tenancy started. Fine up to £20,000 per tenant if not done.</p>
                </div>
              </label>
            </div>
          </div>
        )}

        {/* STEP 5 — Results */}
        {step === 4 && (
          <div>
            <h3 style={{ fontSize: 18, fontWeight: 500, marginBottom: 8 }}>Rent details</h3>
            <p style={{ fontSize: 14, color: 'var(--ink-3)', marginBottom: 24 }}>Set up rent tracking so we can monitor payments and flag arrears.</p>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 6 }}>Monthly rent amount (£)</label>
              <input type="number" min="0" step="1"
                value={rent.monthly_amount}
                onChange={e => setRent(r => ({ ...r, monthly_amount: e.target.value }))}
                placeholder="1200"
                style={{ width: '100%', padding: '10px 14px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 15, background: 'var(--bg)', boxSizing: 'border-box' }}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 6 }}>Rent due day of month</label>
              <select value={rent.due_day} onChange={e => setRent(r => ({ ...r, due_day: e.target.value }))}
                style={{ width: '100%', padding: '10px 14px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 15, background: 'var(--bg)', boxSizing: 'border-box' }}>
                {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                  <option key={d} value={d}>{d}{d === 1 ? 'st' : d === 2 ? 'nd' : d === 3 ? 'rd' : 'th'} of each month</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 6 }}>Payment method</label>
              <select value={rent.payment_method} onChange={e => setRent(r => ({ ...r, payment_method: e.target.value }))}
                style={{ width: '100%', padding: '10px 14px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 15, background: 'var(--bg)', boxSizing: 'border-box' }}>
                <option value="bank_transfer">Bank transfer</option>
                <option value="standing_order">Standing order</option>
                <option value="direct_debit">Direct debit</option>
                <option value="cash">Cash</option>
                <option value="other">Other</option>
              </select>
            </div>

            <p style={{ fontSize: 12, color: 'var(--ink-3)' }}>You can skip this and set it up later from the Rent Tracker.</p>
          </div>
        )}

        {step === 5 && (
          <div className="fade-up">
            <h2 style={{ fontSize: 20, marginBottom: 20 }}>Your compliance report</h2>

            {risks.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px 0' }}>
                <p style={{ fontSize: 40, marginBottom: 12 }}>🎉</p>
                <p style={{ fontWeight: 600, fontSize: 18 }}>All checks passed!</p>
                <p style={{ color: 'var(--ink-3)', fontSize: 14, marginTop: 4 }}>This property appears fully compliant. Keep tracking expiry dates to stay that way.</p>
              </div>
            ) : (
              <>
                {highRisks.length > 0 && (
                  <div style={{ marginBottom: 20 }}>
                    <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--danger)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '.05em' }}>🚨 Action Required ({highRisks.length})</p>
                    {highRisks.map((r, i) => (
                      <div key={i} style={{ background: 'var(--danger-lt)', borderLeft: '3px solid var(--danger)', borderRadius: 8, padding: '12px 16px', marginBottom: 8 }}>
                        <p style={{ fontSize: 14, color: 'var(--danger)' }}>{r.text}</p>
                      </div>
                    ))}
                  </div>
                )}
                {mediumRisks.length > 0 && (
                  <div style={{ marginBottom: 20 }}>
                    <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--warn)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '.05em' }}>⚠️ Attention Needed ({mediumRisks.length})</p>
                    {mediumRisks.map((r, i) => (
                      <div key={i} style={{ background: 'var(--warn-lt)', borderLeft: '3px solid var(--warn)', borderRadius: 8, padding: '12px 16px', marginBottom: 8 }}>
                        <p style={{ fontSize: 14, color: 'var(--warn)' }}>{r.text}</p>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Required checks summary */}
            <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1.5px solid var(--border)' }}>
              <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '.05em' }}>Required for this property</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {requiredChecks.map(c => {
                  const status = certStatuses[c.key] || 'missing';
                  const color = status === 'valid' ? 'var(--accent)' : status === 'missing' || status === 'expired' ? 'var(--danger)' : 'var(--warn)';
                  const icon  = status === 'valid' ? '✅' : status === 'missing' ? '❌' : status === 'expired' ? '🔴' : '🟡';
                  return (
                    <div key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'var(--bg)', borderRadius: 8 }}>
                      <span>{icon}</span>
                      <span style={{ fontSize: 13, color }}>{c.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <p style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 20 }}>⚠️ This report is a guide only and not legal advice. Requirements may vary — consult a professional for complex cases.</p>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button variant="ghost" onClick={step === 0 ? onCancel : () => setStep(s => s - 1)}>
          {step === 0 ? 'Cancel' : '← Back'}
        </Button>
        {step < STEPS.length - 1 ? (
          <Button onClick={() => setStep(s => s + 1)} disabled={
            (step === 0 && (!property.address_line1 || !property.city || !property.postcode))
          }>
            Continue →
          </Button>
        ) : (
          <Button loading={saving} onClick={saveAndFinish}>
            Save property & finish
          </Button>
        )}
      </div>
    </div>
  );
}